import React, {
  ChangeEvent,
  Dispatch,
  FC,
  Fragment,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import VTextInput, { VTextInputResponse } from '../../components/VTextInput/VTextInput'
import { useReportViewEvent, useResponsive } from '../../hooks'
import { fetchPhoneNumberFormatted } from '../../clients/phoneNumberFormatterClient'
import { Salutation } from '../../@types/user'
import { Intent } from '../../@types/intent'
import { BlueHighlighterIcon } from '../../components/icons/BlueHighlighterIcon'
import { fetchValuation } from '../../store/valuation/thunks'
import { sendLead, SendLeadThunkRequest, sendSolarLead } from '../../store/lead/thunks'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import UserConsentModal from './UserConsentModal/UserConsentModal'
import { UserConsentNote } from './UserConsentNote/UserConsentNote'

import {
  updateDataProtectionAccepted,
  updateEmail,
  updateFirstName,
  updateHasNotSeenContactForm,
  updateLastName,
  updatePhoneNumber,
  updateSalutation,
} from '../../store/user/userSlice'
import './ContactStep.scss'
import { RealEstateType } from '../../@types/realEstateType'
import StyledToggleSwitch from '../../components/styled/ToggleSwitch/StyledToggleSwitch'
import StyledRadioButtonHolder from '../../components/styled/radio/RadioButtonHolder'
import { Flow, IntegrationType, Wordings } from '../../@types/config'
import NextButton from '../../components/NextButton/NextButton'
import StyledPopOver from '../../components/styled/PopOver/StyledPopOver'
import { WizardFlowType } from '../../@types/wizardFlowType'
import { RadioButton } from '../../components/RadioButton/RadioButton'
import { ReduxState } from '../../store/reducers'
import { Footer } from '../../components/Footer/Footer'
import { TuvSealSection } from '../../components/TUVSealSection/TUVSealSection'
import { STEP } from '../../@types/state'
import ConsultationBanner from './consultationBanner/ConsultationBanner'
import { reportTrackingEvent } from '../../tracking/thunks'

interface ContactStepProps {
  goToNextStep: () => void
  goToErrorStep: () => void
}

const getHeadLine = (
  realEstateType?: RealEstateType,
  intent?: Intent,
  wordings?: Wordings,
  enableCustomWordings?: boolean,
  extendedBuyerLead?: boolean,
  isConsultation?: boolean
): ReactNode => {
  if (enableCustomWordings && wordings?.contactForm?.headline) {
    return wordings.contactForm.headline
  }
  if (isConsultation) {
    return (
      <>
        Wir <span className='highlighter'>beraten dich gern telefonisch</span> –
        <br className='palm-hide' /> kostenlos & ohne Verpflichtungen.
      </>
    )
  }
  if (realEstateType === RealEstateType.COMMERCIAL) {
    return 'Passende Makler gefunden'
  }
  switch (intent) {
    case Intent.BUY:
      return extendedBuyerLead ? 'Wir haben berechnet, was du dir leisten kannst' : 'Bewertung erfolgreich durchgeführt'
    case Intent.RENT:
      return realEstateType == RealEstateType.LAND
        ? 'Grundstück erfolgreich bewertet'
        : 'Immobilie erfolgreich bewertet'
    case Intent.LET:
    case Intent.MAYBE_MAKLER_LET:
    case Intent.MAKLER_LET:
      return 'Passende Immobilienexperten gefunden'
    case Intent.MAKLER_SALE:
      return 'Passende Makler gefunden'
    case Intent.MAYBE_MAKLER_SALE:
      return 'Passende Makler gefunden'
    case Intent.INDEPENDENT_SALE:
    case Intent.INDEPENDENT_LET:
      return realEstateType == RealEstateType.LAND
        ? 'Grundstück erfolgreich bewertet'
        : 'Immobilie erfolgreich bewertet'
    case Intent.PARTIAL_SALE:
      return 'Angebot für den Teilverkauf'
    case Intent.INDEPENDENT_WITH_OFFER_SALE:
      return 'Passende Käufer gefunden'
    default:
      return 'Passende Makler gefunden'
  }
}

const getSubLine = (
  realEstateType?: RealEstateType,
  intent?: Intent,
  wordings?: Wordings,
  enableCustomWordings?: boolean,
  extendedBuyerLead?: boolean,
  isConsultation?: boolean
) => {
  if (enableCustomWordings && wordings?.contactForm?.subHeadline) {
    return wordings.contactForm.subHeadline
  }
  if (isConsultation) {
    return 'Wie können wir dich am besten erreichen?'
  }
  if (realEstateType === RealEstateType.COMMERCIAL) {
    return 'Wer soll die Empfehlungen erhalten?'
  }
  switch (intent) {
    case Intent.BUY:
      return extendedBuyerLead
        ? 'Wer soll den Finanzierungsrahmen und passende Immobilienangebote erhalten?'
        : 'Wer soll die Bewertung und eine unverbindliche Beratung erhalten?'
    case Intent.RENT:
      return 'Wer soll die Bewertung erhalten?'
    case Intent.INDEPENDENT_LET:
      return 'Wer soll den Immobilienwert und eine unverbindliche Beratung erhalten?'
    case Intent.LET:
    case Intent.MAYBE_MAKLER_LET:
    case Intent.MAKLER_LET:
      return 'Wer soll die Empfehlung erhalten?'
    case Intent.INDEPENDENT_SALE:
      return realEstateType == RealEstateType.LAND
        ? 'Wer soll den Grundstückswert und eine unverbindliche Verkaufsberatung erhalten?'
        : 'Wer soll den Immobilienwert und eine unverbindliche Verkaufsberatung erhalten?'
    case Intent.PARTIAL_SALE:
      return 'Du erhältst eine individuelle Beratung und ein Angebot zum Teilverkauf.'
    case Intent.INDEPENDENT_WITH_OFFER_SALE:
      return `Wer soll die unverbindlichen Angebote erhalten?`
    case Intent.MAKLER_SALE:
      return 'Du erhältst bis zu 3 Empfehlungen für passende Makler, die dich individuell beraten werden.'
  }
}

const getButtonString = (
  realEstateType?: RealEstateType,
  intent?: Intent,
  extendedBuyerLead?: boolean,
  isConsultation?: boolean
) => {
  if (isConsultation) {
    return 'Kostenlos beraten lassen'
  }
  if (realEstateType === RealEstateType.COMMERCIAL) {
    return 'Empfehlungen erhalten'
  }
  switch (intent) {
    case Intent.RENT:
    case Intent.INDEPENDENT_SALE:
      return 'Bewertung erhalten'
    case Intent.PARTIAL_SALE:
      return 'Angebot anfordern'
    case Intent.LET:
    case Intent.MAKLER_SALE:
    case Intent.MAYBE_MAKLER_SALE:
      return 'Empfehlungen erhalten'
    case Intent.INDEPENDENT_WITH_OFFER_SALE:
      return 'Angebote erhalten'
    case Intent.BUY:
      return extendedBuyerLead ? 'Angebote erhalten' : 'Bewertung erhalten'
    case Intent.INDEPENDENT_LET:
      return 'Bewertung erhalten'
    default:
      return ''
  }
}

export const ContactStep: FC<ContactStepProps> = (props) => {
  const pageTitle = 'contactform'
  useReportViewEvent({ pageTitle })
  const { isMobile } = useResponsive()
  const {
    config: { theme, illustration, integrationType, wordings, flow },
    flow: { wizardFlowType },
    user,
    lead: { realEstateType, intent, extendedBuyerLead, isConsultationAccepted: isConsultation },
  } = useSelector((state: ReduxState) => state)
  const enableCustomWordings = useMemo(() => {
    return (
      intent &&
      [Intent.MAKLER_SALE, Intent.MAYBE_MAKLER_SALE].includes(intent) &&
      realEstateType &&
      realEstateType !== RealEstateType.COMMERCIAL
    )
  }, [intent, realEstateType])

  const [firstNameValid, setFirstNameValid] = useState<boolean>(false)
  const [lastNameValid, setLastNameValid] = useState<boolean>(false)
  const [phoneNumberValid, setPhoneNumberValid] = useState<boolean>(false)
  const [emailValid, setEmailValid] = useState<boolean>(false)
  const [userConsentVisibility, setUserConsentVisibility] = useState<boolean>(false)
  const [isHovering, setIsHovering] = useState<boolean>(false)
  const [isNextEnabled, setIsNextEnabled] = useState(true)

  const { email, firstName, lastName, phoneNumber } = user
  const showHighlighter =
    !isConsultation &&
    !(integrationType === IntegrationType.EXTERNAL) &&
    !(integrationType === IntegrationType.IMMOVERKAUF24)

  const isPdfWordingChangeEnabled: boolean =
    wizardFlowType === WizardFlowType.IBW &&
    (realEstateType === RealEstateType.HOUSE || realEstateType === RealEstateType.APARTMENT)
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24

  const dispatch = useDispatch()

  const headline = getHeadLine(
    realEstateType,
    intent,
    wordings,
    enableCustomWordings,
    extendedBuyerLead,
    isConsultation
  )
  const subLine = getSubLine(realEstateType, intent, wordings, enableCustomWordings, extendedBuyerLead, isConsultation)
  const buttonString = getButtonString(realEstateType, intent, extendedBuyerLead, isConsultation)

  useEffect(() => {
    if (realEstateType != RealEstateType.COMMERCIAL) {
      dispatch(fetchValuation({ valuationForStep: STEP.ADDRESS }))
    }
  }, [])

  const isInputValid = emailValid && firstNameValid && lastNameValid && phoneNumberValid

  const validatePhoneNumber = (phoneNumberString: string): Promise<{ valid: boolean; formatted?: string }> => {
    if (phoneNumberString.length < 4) return Promise.resolve({ valid: false })

    return fetchPhoneNumberFormatted(phoneNumberString)
      .then((res) => ({ valid: true, formatted: res.formattedNumber }))
      .catch(() => ({ valid: false }))
  }

  const handleSubmit = () => {
    setIsNextEnabled(false)
    const callbacks: SendLeadThunkRequest = {
      successCallback: props.goToNextStep,
      errorCallback: props.goToErrorStep,
    }
    if (flow === Flow.SOLAR) {
      dispatch(
        reportTrackingEvent({
          pageTitle: 'contactform',
          label: 'solar',
          eventName: 'lead_submit',
          inputValue: '',
          customDimensions: { ga_cd_lead_engine_input_value: '' },
          action: 'solar-leadengine',
          category: 'lead',
          flow: flow,
        })
      )
    }
    dispatch(wizardFlowType === WizardFlowType.SOLAR ? sendSolarLead(callbacks) : sendLead(callbacks))
    dispatch(updateHasNotSeenContactForm(false))
  }

  const handleVInputChange = (
    response: VTextInputResponse,
    setValid: Dispatch<SetStateAction<boolean>>,
    updateState: (value: string) => unknown
  ) => {
    if (response.valid && response.value) {
      dispatch(updateState(response.value))
    }
    setValid(response.valid)
  }

  const salutationLabel = {
    [Salutation.HERR]: 'Herr',
    [Salutation.FRAU]: 'Frau',
  }

  const PopUpDiv = () => {
    return (
      <StyledPopOver
        id='rle-form-pop-over'
        className='margin pop-up'
        visible={isHovering}
        bodyPosition='top'
        arrowPosition='center'
        color={theme.text ? theme.text : 'black'}
        secondaryLight={theme.secondaryLight}
      >
        <div className='pop-up-box'>
          <p className='status-list font-bold'>Tochterunternehmen:</p>
          <ul className='status-list list-bullet'>
            <li>
              <p>immoverkauf24 GmbH</p>
            </li>
            <li style={{ paddingTop: '5px' }}>
              <p>Zenhomes GmbH</p>
            </li>
          </ul>
        </div>
      </StyledPopOver>
    )
  }

  const handleMouseOver = () => {
    setIsHovering(true)
  }

  const handleMouseOut = () => {
    setIsHovering(false)
  }

  const PDFTeaserIcon = () => {
    return (
      <div className='icons'>
        <div className='icon-container blue-bg'>
          <BlueHighlighterIcon />
        </div>
        <i className='font-m is24-icon-pdf' />
      </div>
    )
  }

  const showPDFTeaser = () =>
    (realEstateType === RealEstateType.HOUSE ||
      realEstateType === RealEstateType.APARTMENT ||
      (realEstateType == RealEstateType.LAND && isConsultation)) &&
    !isIv24Integration &&
    wizardFlowType != WizardFlowType.SOLAR &&
    !extendedBuyerLead

  const showFooter = () =>
    (realEstateType &&
      [RealEstateType.HOUSE, RealEstateType.APARTMENT, RealEstateType.LAND].includes(realEstateType) &&
      [Intent.MAKLER_SALE, Intent.MAYBE_MAKLER_SALE].includes(intent as Intent)) ||
    (intent === Intent.BUY && extendedBuyerLead) ||
    wizardFlowType === WizardFlowType.SOLAR

  return (
    <>
      <StepTemplate
        headline={wizardFlowType === WizardFlowType.SOLAR ? 'Passender Anbieter gefunden' : headline}
        subLine={
          wizardFlowType === WizardFlowType.SOLAR ? 'Wohin dürfen wir dein unverbindliches Angebot senden?' : subLine
        }
        highlighter={showHighlighter}
        footer={isMobile && showFooter() ? <Footer /> : undefined}
        displayNext={false}
        displayBack={false}
        isNextEnabled={isInputValid && isNextEnabled}
        goToNextStep={handleSubmit}
        buttonString={buttonString === '' ? 'Empfehlungen erhalten' : buttonString}
        buttonResponsive
        buttonAppearance='primary'
        className='contact-form'
        dataTestingButton='contact-form-submit-button'
        backgroundColor={theme.background}
        primary={theme.primary}
        textColor={theme.text}
        secondaryLight={theme.secondaryLight}
        illus={illustration}
        noMarginTop={isConsultation}
      >
        <div className='grid-flex'>
          <div className='grid-item desk-one-third' />
          <div className='grid-item desk-one-third data-container'>
            <div
              className='grid-item margin-bottom-m'
              onChange={(e: ChangeEvent<HTMLInputElement>) => dispatch(updateSalutation(e.target.value as Salutation))}
            >
              {showPDFTeaser() && (
                <div className='teasers hide-on-desk' data-testid='pdf-teaser-container'>
                  {isConsultation ? (
                    <></>
                  ) : (
                    <>
                      <div className='grid-flex gutter-s grid-justify-center'>
                        <div className='grid-item '>
                          <PDFTeaserIcon />
                        </div>
                        <div className='grid-item '>
                          <h6>Mit aktueller Werteinschätzung</h6>
                        </div>
                      </div>

                      <div className='grid-flex gutter-s grid-justify-center'>
                        <div className='txt'>
                          {isPdfWordingChangeEnabled
                            ? extendedBuyerLead
                              ? 'Immobilienspezialisten von ImmoScout24 beraten Sie'
                              : 'Du erhältst zusätzlich eine kostenlose Erstbewertung zum Download.'
                            : extendedBuyerLead
                            ? 'Immobilienspezialisten von ImmoScout24 beraten Sie'
                            : 'Du erhältst zusätzlich eine kostenlose Erstbewertung mit Details zur Berechnung als PDF.'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              <StyledRadioButtonHolder
                backgroundColor={theme.background}
                checkedColor={isIv24Integration ? theme.secondary : theme.primary}
              >
                <div className='inline-block radio-button-wrapper'>
                  <RadioButton
                    label={<span className='font-bold'>{salutationLabel[Salutation.HERR]}</span>}
                    id='herrField'
                    name='salutation'
                    value={Salutation.HERR}
                    defaultChecked
                  />
                </div>
                <div className='inline-block'>
                  <RadioButton
                    label={<span className='font-bold'>{salutationLabel[Salutation.FRAU]}</span>}
                    id='frauField'
                    name='salutation'
                    value={Salutation.FRAU}
                  />
                </div>
              </StyledRadioButtonHolder>
            </div>
            <div className='grid-item grid grid-flex gutter-m align-center'>
              <div className='grid-item one-half palm-one-whole fade-in-error-message-no-label input-error-fade'>
                <VTextInput
                  placeholder='Vorname'
                  id='firstName'
                  testId='firstName'
                  label=''
                  initialValue={firstName}
                  reportingData={{
                    label: 'error_contactform_firstname',
                    pageTitle,
                    eventName: 'lead_error',
                    inputValue: 'firstname',
                  }}
                  validation={{
                    regExp: /^\D+$/,
                  }}
                  errorMessage='Bitte gib nur Buchstaben an.'
                  maxLength={30}
                  onChange={(response) => handleVInputChange(response, setFirstNameValid, updateFirstName)}
                  validateOnMount={firstName !== ''}
                  className={'input-text'}
                />
              </div>
              <div className='grid-item one-half palm-one-whole fade-in-error-message-no-label input-error-fade'>
                <VTextInput
                  placeholder='Nachname'
                  id='lastName'
                  testId='lastName'
                  label=''
                  initialValue={lastName}
                  reportingData={{
                    label: 'error_contactform_lastname',
                    pageTitle,
                    eventName: 'lead_error',
                    inputValue: 'lastname',
                  }}
                  onChange={(response) => handleVInputChange(response, setLastNameValid, updateLastName)}
                  validation={{
                    regExp: /^\D+$/,
                  }}
                  errorMessage={'Bitte gib nur Buchstaben an.'}
                  maxLength={30}
                  validateOnMount={lastName !== ''}
                  className={'input-text'}
                />
              </div>
              <div className='grid-item one-whole fade-in-error-message-no-label input-error-fade'>
                <VTextInput
                  placeholder='Telefonnummer'
                  id='phoneNumber'
                  testId='phoneNumber'
                  label=''
                  type='tel'
                  validation={{
                    validationPromise: validatePhoneNumber,
                  }}
                  reportingData={{
                    label: 'error_contactform_phonenumber',
                    pageTitle,
                    eventName: 'lead_error',
                    inputValue: 'phonenumber',
                  }}
                  errorMessage={'Bitte gib eine gültige Telefonnummer an.'}
                  onChange={(response) => handleVInputChange(response, setPhoneNumberValid, updatePhoneNumber)}
                  initialValue={phoneNumber}
                  validateOnMount={phoneNumber !== ''}
                  className={'input-text'}
                />
              </div>
              <div className='grid-item one-whole margin-bottom-l fade-in-error-message-no-label input-error-fade'>
                <VTextInput
                  placeholder='E-Mail-Adresse'
                  id='emailAddress'
                  testId='emailAddress'
                  label=''
                  type='email'
                  initialValue={email}
                  validation={{
                    regExp:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  }}
                  reportingData={{
                    label: 'error_contactform_email',
                    pageTitle,
                    eventName: 'lead_error',
                    inputValue: 'email',
                  }}
                  onChange={(response) => handleVInputChange(response, setEmailValid, updateEmail)}
                  errorMessage='Bitte gib eine gültige Mail-Adresse an.'
                  maxLength={50}
                  validateOnMount={email !== ''}
                  className={'input-text'}
                />
              </div>
            </div>
            <div className='grid-item legal-note margin-bottom-xl'>
              <UserConsentNote
                realEstateType={realEstateType}
                intent={intent}
                isConsultationAccepted={isConsultation}
                onLinkClick={() => setUserConsentVisibility(true)}
                extendedBuyerLead={extendedBuyerLead}
              />
            </div>
            {!isIv24Integration && (
              <div className='grid-item legal-note grid grid-flex grid-fill-rows' data-testid='legal-note'>
                <div
                  className={`grid-item grid-item-fixed-width toggleSwitch`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    dispatch(updateDataProtectionAccepted(event.target.checked))
                  }
                >
                  <StyledToggleSwitch
                    id='rle-form-checkbox'
                    label=''
                    backgroundColor={theme.primaryLight}
                    checkedColor={theme.primary}
                  />
                </div>
                {extendedBuyerLead ? (
                  <div className='grid-item one-half'>
                    Ich möchte auf mich zugeschnittene Angebote und Neuigkeiten der Immobilien Scout GmbH per E-Mail
                    erhalten. Abmelden geht jederzeit.
                    <br /> Weitere Informationen
                  </div>
                ) : (
                  <div className='grid-item one-half'>
                    Ich stimme der Verarbeitung meiner Daten durch die Immobilien Scout GmbH und{' '}
                    {isHovering && <PopUpDiv />}
                    <div style={{ display: 'contents' }} onMouseEnter={handleMouseOver} onMouseLeave={handleMouseOut}>
                      {isMobile ? (
                        <u>Tochterunternehmen</u>
                      ) : (
                        <u>
                          Tochter-
                          <br />
                          unternehmen
                        </u>
                      )}
                    </div>{' '}
                    wie in der{' '}
                    <a
                      className='link'
                      rel='noopener noreferrer'
                      target='_blank'
                      href='https://www.immobilienscout24.de/agb/datenschutz/Datenschutzeinwilligung.html'
                      data-testid='legal-note-link'
                    >
                      Einwilligungserklärung
                    </a>{' '}
                    beschrieben zum Zweck personalisierter E-Mail-Werbung zu. Widerrufen geht jederzeit.
                  </div>
                )}
              </div>
            )}
          </div>
          <div className='grid-item desk-one-third gutter-vertical-l hide-on-palm-and-lap'>
            <div className='grid-flex grid-fill-rows'>
              <div className='grid-item palm-one-whole lap-one-whole desk-one-third' />
              <div className='grid-item palm-one-whole lap-one-whole desk-two-third'>
                {showPDFTeaser() && (
                  <div className='teasers' data-testid='pdf-teaser'>
                    <Fragment>
                      {isConsultation ? (
                        <>
                          Stell uns <span className='font-bold'> jede Frage</span> rund um deine Immobilie. Wir beraten
                          dich <span className='font-bold'> individuell</span>.
                        </>
                      ) : (
                        <>
                          <PDFTeaserIcon />
                          {extendedBuyerLead ? (
                            <h6>Mit persönlicher Beratung</h6>
                          ) : (
                            <h6>Mit aktueller Werteinschätzung</h6>
                          )}
                          <span>
                            {isPdfWordingChangeEnabled
                              ? 'Du erhältst zusätzlich eine kostenlose Erstbewertung zum Download.'
                              : 'Du erhältst zusätzlich eine kostenlose Erstbewertung mit Details zur Berechnung als PDF.'}
                          </span>
                        </>
                      )}
                    </Fragment>
                    <div className='padding-top-l'>{extendedBuyerLead ? '' : <TuvSealSection />}</div>
                  </div>
                )}
                {wizardFlowType === WizardFlowType.SOLAR && (
                  <div className='solar-teaser' data-testid='pdf-teaser'>
                    <Fragment>
                      <h5>Unverbindliches Angebot für dein Komplettpaket inkl. Montage.</h5>
                      <span>
                        <h6>Wir empfehlen nur geprüfte Anbieter.</h6>
                      </span>
                    </Fragment>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <NextButton
          buttonText={buttonString === '' ? 'Empfehlungen erhalten' : buttonString}
          buttonAppearance='primary'
          responsive={true}
          onClick={handleSubmit}
          dataTestingButton='contact-form-submit-button'
          disabled={!isInputValid || !isNextEnabled}
          customClasses='margin-top-xl'
        />
        {intent === Intent.PARTIAL_SALE && (
          <div
            className='grid-item desk-one-whole lap-one-whole margin-top-xxl'
            data-testid='contact-step-partial-sale-benefits'
          >
            <div className={`grid-item  lap-one-third desk-one-third `}>
              <div className='grid grid-flex gutter-s grid-justify-center' style={{ width: '22em' }}>
                <div className='grid-item '>
                  <i
                    className='font-xl is24-icon-positive-check'
                    style={{ color: theme.primary ? theme.primary : '#00d9b1' }}
                  />
                </div>
                <div className='grid-item'>Individuelle Beratung</div>
              </div>
            </div>
            <div className='grid-item  lap-one-third lap-padding-left-xl desk-one-third  '>
              <div className='grid grid-flex gutter-s grid-justify-center' style={{ width: '22em' }}>
                <div className='grid-item  '>
                  <i
                    className='font-xl is24-icon-positive-check'
                    style={{ color: theme.primary ? theme.primary : '#00d9b1' }}
                  />
                </div>
                <div className='grid-item'>Unverbindliches Angebot</div>
              </div>
            </div>
            <div className='grid-item  lap-one-third desk-one-third'>
              <div className='grid grid-flex gutter-s grid-justify-center' style={{ width: '22em' }}>
                <div className='grid-item'>
                  <i
                    className='font-xl is24-icon-positive-check'
                    style={{ color: theme.primary ? theme.primary : '#00d9b1' }}
                  />
                </div>
                <div className='grid-item'>Ohne versteckte Kosten</div>
              </div>
            </div>
          </div>
        )}
        {isConsultation && <ConsultationBanner />}
        <UserConsentModal visibility={userConsentVisibility} setVisibility={setUserConsentVisibility} />
      </StepTemplate>
      {showFooter() && !isMobile ? <Footer /> : undefined}
    </>
  )
}

export default ContactStep
